import { useState } from 'react'

import { Box, SortDirection, Table, Text } from '@factorialco/deprecated-design-system'
import { EmployeeTimePeriodDetailsFragment } from 'generated/resources/graphql'
import { orderBy, range } from 'lodash'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import i18n from 'modules/core/lib/i18n'

import {
  MAX_TO_SHOW,
  ZERO_HOURS,
  getUserInfo,
  sortByName,
} from 'modules/shift_management/components/ShiftManagement/TimeInsightsSidepanel/Content/utils'

import LoadingSkeleton from './LoadingSkeleton'

interface IEmployeeTimePeriodDetails {
  employeeFullName: string
  paidLeavesHours: string
  recommendedHours: string
  plannedHours: string
  totalBalanceHours: string
}

const orderData = createComputed(
  (
    timePeriodDetails: EmployeeTimePeriodDetailsFragment[],
    attribute: keyof IEmployeeTimePeriodDetails,
    direction: SortDirection
  ) => {
    const findAttribute = (item: EmployeeTimePeriodDetailsFragment) => {
      if (attribute === 'employeeFullName') {
        return sortByName(item)
      }

      return Number(item[attribute])
    }

    return orderBy(
      timePeriodDetails,
      (item: EmployeeTimePeriodDetailsFragment) => findAttribute(item),
      [direction]
    )
  }
)

interface Props {
  timePeriodDetails: EmployeeTimePeriodDetailsFragment[]
  loading: boolean
  convertHours: boolean
}

interface TitleProps {
  name: string
  colSpan: number
  attribute: string
}

const AnnualBalanceTable = ({ timePeriodDetails, loading, convertHours }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [direction, setDirection] = useState<SortDirection>('asc')
  const [attribute, setAttribute] = useState<keyof IEmployeeTimePeriodDetails>('employeeFullName')
  const showMore = i18n.t(
    'shift_management.time_insights.time_balance_content.planned_time.table.show_more'
  )
  const showLess = i18n.t(
    'shift_management.time_insights.time_balance_content.planned_time.table.show_less'
  )

  const headerTitles: TitleProps[] = [
    {
      name: i18n.t(
        'shift_management.time_insights.time_balance_content.planned_time.table.employee_header'
      ),
      colSpan: 5,
      attribute: 'employeeFullName',
    },
    {
      name: i18n.t(
        'shift_management.time_insights.time_balance_content.annual_balance.table.paid_leaves'
      ),
      colSpan: 3,
      attribute: 'paidLeavesHours',
    },
    {
      name: i18n.t(
        'shift_management.time_insights.time_balance_content.annual_balance.table.max_yearly_hours'
      ),
      colSpan: 3,
      attribute: 'recommendedHours',
    },
    {
      name: i18n.t(
        'shift_management.time_insights.time_balance_content.annual_balance.table.planned'
      ),
      colSpan: 3,
      attribute: 'plannedHours',
    },
    {
      name: i18n.t(
        'shift_management.time_insights.time_balance_content.annual_balance.table.additional'
      ),
      colSpan: 3,
      attribute: 'additionalHours',
    },
    {
      name: i18n.t(
        'shift_management.time_insights.time_balance_content.annual_balance.table.balance'
      ),
      colSpan: 3,
      attribute: 'totalBalanceHours',
    },
  ]

  const onSort = (sortAttribute: string, sortDirection: SortDirection) => {
    setDirection(sortDirection)
    setAttribute(sortAttribute as keyof IEmployeeTimePeriodDetails)
  }

  const sortedEmployees = orderData(timePeriodDetails, attribute, direction).get()
  const employeesWithData = isExpanded ? sortedEmployees : sortedEmployees.slice(0, MAX_TO_SHOW)
  const employeesCount = range(employeesWithData.length)

  const optionsBottom =
    sortedEmployees.length > MAX_TO_SHOW
      ? () => {
          return (
            <Box width="full">
              <Box alignSelf="center" cursor="pointer" onClick={() => setIsExpanded(!isExpanded)}>
                <Text.Normal size="200" weight="semibold">
                  {isExpanded ? showLess : showMore}
                </Text.Normal>
              </Box>
            </Box>
          )
        }
      : undefined

  return (
    <Table optionsBottom={optionsBottom}>
      <Table.Head>
        <Table.Row>
          {headerTitles.map((headerTitle) => (
            <Table.SortableHeadCell
              colSpan={headerTitle.colSpan}
              key={headerTitle.name}
              attribute={headerTitle.attribute}
              onSort={onSort}
              sortAttribute={attribute}
              sortDirection={direction}
            >
              <Box alignItems="center" textAlign="left">
                <Text.Normal size="200" weight="semibold" ellipsis>
                  {headerTitle.name}
                </Text.Normal>
              </Box>
            </Table.SortableHeadCell>
          ))}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {loading
          ? employeesCount.map((i: number) => (
              <Table.Row key={i}>
                <LoadingSkeleton />
              </Table.Row>
            ))
          : employeesWithData.map((employeeData) => (
              <Table.Row key={employeeData.id}>
                <Table.Cell colSpan={5}>
                  {getUserInfo(employeeData.employeeFullName, employeeData.id)}
                </Table.Cell>
                <Table.Cell colSpan={3}>{Number(employeeData.paidLeavesHours)}</Table.Cell>
                <Table.Cell colSpan={3}>{Number(employeeData.recommendedHours)}</Table.Cell>
                <Table.Cell colSpan={3}>{Number(employeeData.standardPlannedHours)}</Table.Cell>
                <Table.Cell colSpan={3}>
                  {convertHours ? Number(employeeData.additionalPlannedHours) : Number(ZERO_HOURS)}
                </Table.Cell>
                <Table.Cell colSpan={3}>
                  {convertHours
                    ? Number(employeeData.totalBalanceHours)
                    : Number(employeeData.totalBalanceHoursWithoutAdditional)}
                </Table.Cell>
              </Table.Row>
            ))}
      </Table.Body>
    </Table>
  )
}

export default observer(AnnualBalanceTable)
